import {Injectable} from '@angular/core';

export interface ReportMetaItem {
  label: string;
  value: string;
}

export interface ReportPrintOptions {
  title: string;
  contentHtml: string;
  meta?: ReportMetaItem[];
  orientation?: 'portrait' | 'landscape';
  noHeader?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ReportPrintService {

  constructor() {
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  buildHtml(options: ReportPrintOptions): string {
    const {title, contentHtml, meta = [], orientation = 'landscape', noHeader = false} = options;
    const runAt = new Date().toLocaleString();
    const currentUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('currentUser') || 'null');
      } catch {
        return null;
      }
    })();
    const userName: string = currentUser?.name || currentUser?.username || '';
    // Prefer the human-readable company name (cached by the header component once it
    // resolves it from the `company` API), falling back to the raw database name only
    // if that cache isn't available for some reason (e.g. very first load before the
    // header has had a chance to run).
    const companyName: string = localStorage.getItem('currentCompanyName') || currentUser?.companyDbName || '';

    const metaItems: ReportMetaItem[] = [...meta];

    const metaChips = metaItems.length
      ? metaItems.map(m => `<span class="chip"><strong>${this.escapeHtml(m.label)}:</strong>&nbsp;${this.escapeHtml(m.value)}</span>`).join('')
      : '';

    const styles = `
      <style>
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @page { size: ${orientation}; margin: 12mm; }
        *, *::before, *::after { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        html, body { margin: 0; padding: 0; background: #f3f4f6; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        body { font-family: Arial, Helvetica, sans-serif; color: #111827; }

        /* ── Header band ── */
        .rpt-header {
          background: transparent;
          color: #111827;
          padding: 14px 20px 10px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #1e3a5f;
        }
        .rpt-header-left { display: flex; flex-direction: column; gap: 2px; }
        .rpt-company { font-size: 11px; font-weight: 400; color: #6b7280; letter-spacing: 0.04em; text-transform: uppercase; }
        .rpt-title { font-size: 20px; font-weight: 700; margin: 0; letter-spacing: -0.01em; color: #1e3a5f; }
        .rpt-header-right { text-align: right; font-size: 11px; color: #6b7280; line-height: 1.6; }
        .rpt-run-date { font-size: 11px; color: #6b7280; margin-top: 4px; }

        /* ── Filter chips ── */
        .rpt-chips {
          background: transparent;
          border-bottom: 1px solid #d0d7de;
          padding: 6px 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          min-height: 30px;
        }
        .chip {
          background: transparent;
          border: 1px solid #9ca3af;
          border-radius: 12px;
          padding: 2px 10px;
          font-size: 11px;
          color: #374151;
          white-space: nowrap;
        }

        /* ── Report card ── */
        .rpt-card {
          border: 1px solid #d0d7de;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
          margin: 16px 0;
          box-shadow: 0 1px 4px rgba(0,0,0,0.07);
        }
        .rpt-body { padding: 10px 14px 14px; overflow-x: auto; }

        /* ── Table ── */
        table { width: 100%; border-collapse: collapse; }
        thead th {
          background: #f6f8fa;
          border-bottom: 2px solid #d0d7de;
          font-size: 11px;
          font-weight: 700;
          color: #1e3a5f;
          padding: 7px 8px;
          text-align: left;
          white-space: nowrap;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
        tbody td {
          border-bottom: 1px solid #e5e7eb;
          padding: 5px 8px;
          font-size: 11px;
          color: #111827;
          vertical-align: top;
        }
        tbody tr:nth-child(even) td { background: #f9fafb; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        tbody tr:last-child td { border-bottom: none; }

        /* ── Footer ── */
        .rpt-footer {
          border-top: 1px solid #e5e7eb;
          padding: 8px 16px;
          font-size: 10px;
          color: #9ca3af;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .rpt-footer-confidential { font-style: italic; }

        /* ── Print overrides ── */
        @media print {
          html, body { background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
          .rpt-card { box-shadow: none; margin: 0; border-radius: 0; border: none; }
          .rpt-header { border-radius: 0; }
          thead th { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
          tbody tr:nth-child(even) td { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
          .no-print { display: none !important; }
          thead { display: table-header-group; }
          tbody tr { page-break-inside: avoid; }
        }
      </style>
    `;

    const headerBand = noHeader ? '' : `
        <div class="rpt-header">
          <div class="rpt-header-left">
            ${companyName ? `<div class="rpt-company">${this.escapeHtml(companyName)}</div>` : ''}
            <div class="rpt-title">${this.escapeHtml(title)}</div>
            <div class="rpt-run-date">Run: ${this.escapeHtml(runAt)}</div>
          </div>
          <div class="rpt-header-right">
            ${userName ? `<div>Prepared by: <strong>${this.escapeHtml(userName)}</strong></div>` : ''}
          </div>
        </div>
        ${metaChips ? `<div class="rpt-chips">${metaChips}</div>` : ''}`;

    const header = `
      <div class="rpt-card">
        ${headerBand}
        <div class="rpt-body">${contentHtml}</div>
        <div class="rpt-footer">
          <div class="rpt-footer-confidential">Confidential &mdash; For internal use only</div>
          <div>AcctXERP</div>
        </div>
      </div>
    `;

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${this.escapeHtml(title)}</title>
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" />
    ${styles}
  </head>
  <body>
    ${header}
  </body>
</html>`;
  }

  printInIframe(html: string): void {
    // Use a hidden srcdoc iframe — page stays visible, only the print dialog appears.
    // srcdoc is same-origin so all styles including backgrounds render correctly.
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:0;';
    iframe.setAttribute('srcdoc', html);
    document.body.appendChild(iframe);

    iframe.addEventListener('load', () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => {
          try {
            iframe.remove();
          } catch { /* noop */
          }
        }, 2000);
      }
    });
  }

  escapeHtml(text: string): string {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /** Strip d-none classes and inline display:none from captured table HTML */
  cleanContentHtml(html: string): string {
    if (!html) {
      return '';
    }
    try {
      html = html.replace(/class="([^"]*)"/g, (_m, cls) => {
        const newCls = String(cls).split(/\s+/).filter((c: string) => c && c !== 'd-none').join(' ');
        return `class="${newCls}"`;
      });
      html = html.replace(/style="([^"]*)"/g, (_m, style) => {
        const newStyle = String(style).split(';').map(s => s.trim())
          .filter(s => s && !/^display\s*:\s*none\s*$/i.test(s)).join('; ');
        return `style="${newStyle}"`;
      });
    } catch { /* noop */
    }
    return html;
  }
}
